import { Close, Save, Settings } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postRelayActions } from '@sophocles/entities/Site';

function RelayInfoCard({
  dr,
  relayInfo,
  expectedReadings,
  dispatch,
  siteId,
  fixtures,
  isBillboard,
  hideVoltage,
  hideVoltageEdit,
}) {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [editVoltage, setEditVoltage] = useState(null);
  const [editLoads, setEditLoads] = useState([]);
  const [editPowerActive, setEditPowerActive] = useState(true);

  const allLoads = dr.relayNumbers.flatMap(rn => relayInfo[rn]?.loads || []);
  const voltage = relayInfo[dr.relayNumbers[0]]?.voltage;
  const allActive = dr.relayNumbers.every(rn => relayInfo[rn]?.powerMeasurementActive !== false);
  const primaryRelayId = relayInfo[dr.relayNumbers[0]]?.id;

  const fixtureList = fixtures?.toList().toJS() || [];

  const handleEnterEditMode = () => {
    setEditVoltage(voltage);
    setEditLoads(allLoads.map(l => ({ ...l })));
    setEditPowerActive(allActive);
    setEditMode(true);
  };

  const handleSave = () => {
    const payload = {
      actions: [{ loads: [], relays: [], contracts: [], schedules: [] }],
    };

    // Update relay voltage and power measurement
    // Skip if primaryRelayId is missing — relayInfo hasn't loaded for this
    // relay slot yet and the backend rejects modify without an id.
    if (primaryRelayId != null && (editVoltage !== voltage || editPowerActive !== allActive)) {
      payload.actions[0].relays.push({
        action: 'modify',
        id: primaryRelayId,
        ...(editVoltage !== voltage && { voltage: editVoltage }),
        ...(editPowerActive !== allActive && { power_measurement_active: editPowerActive }),
      });
    }

    // Update loads
    editLoads.forEach((load, i) => {
      const original = allLoads[i];
      if (load.fixtureCount !== original?.fixtureCount || load.fixtureId !== original?.fixtureId) {
        payload.actions[0].loads.push({
          action: 'modify',
          id: load.id,
          fixture_id: load.fixtureId,
          number_of_fixtures: load.fixtureCount,
          primary_relay_id: primaryRelayId,
        });
      }
    });

    if (payload.actions[0].loads.length > 0 || payload.actions[0].relays.length > 0) {
      dispatch(postRelayActions(siteId, payload));
    }
    setEditMode(false);
  };

  const updateLoad = (index, field, value) => {
    setEditLoads(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'fixtureId') {
        const fixture = fixtureList.find(f => f.id === value);
        if (fixture) {
          updated[index].fixtureType = fixture.name;
          updated[index].fixtureWatts = fixture.watts;
        }
      }
      return updated;
    });
  };

  if (editMode) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 220px', minWidth: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" fontWeight={600}>
            {dr.label}
          </Typography>
          <Box display="flex" gap={0.5}>
            <Tooltip title={t('actions.cancel')}>
              <IconButton size="small" onClick={() => setEditMode(false)}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('actions.save')}>
              <IconButton size="small" onClick={handleSave} color="primary">
                <Save fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Stack spacing={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Switch size="small" checked={editPowerActive} onChange={e => setEditPowerActive(e.target.checked)} />
            <Typography variant="caption">{t('measurements.powerMeasurement')}</Typography>
          </Box>
          {!hideVoltage && !hideVoltageEdit && (
            <TextField
              label={t('measurements.voltage')}
              type="number"
              size="small"
              value={editVoltage ?? ''}
              onChange={e => setEditVoltage(parseInt(e.target.value, 10) || 0)}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">V</InputAdornment> },
              }}
              sx={{ width: '100px' }}
            />
          )}
          {editLoads.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {isBillboard ? t('measurements.panels') : t('measurements.zones')}
              </Typography>
              <Stack spacing={1}>
                {editLoads.map((load, i) => (
                  <Box key={load.id || i} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>
                      {load.name}
                    </Typography>
                    {isBillboard && (
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <FormControl size="small" variant="standard" sx={{ flex: 1, minWidth: 0 }}>
                          <InputLabel>{t('measurements.fixture')}</InputLabel>
                          <Select
                            value={load.fixtureId || ''}
                            label={t('measurements.fixture')}
                            onChange={e => updateLoad(i, 'fixtureId', e.target.value)}
                          >
                            {fixtureList.map(f => (
                              <MenuItem key={f.id} value={f.id}>
                                {f.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label={t('measurements.qty')}
                          type="number"
                          size="small"
                          variant="standard"
                          value={load.fixtureCount}
                          onChange={e => updateLoad(i, 'fixtureCount', parseInt(e.target.value, 10) || 0)}
                          sx={{ width: '50px' }}
                        />
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    );
  }

  const isEditable = primaryRelayId != null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        flex: '1 1 180px',
        minWidth: 0,
        ...(isEditable && {
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }),
      }}
      onClick={isEditable ? handleEnterEditMode : undefined}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" fontWeight={600}>
          {dr.label}
        </Typography>
        {isEditable && <Settings sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5 }} />}
      </Box>
      <Box sx={{ mt: 0.5, lineHeight: 1.4 }}>
        {!allActive && (
          <Typography variant="caption" color="warning.main" fontStyle="italic" component="div">
            {t('measurements.measurementOff')}
          </Typography>
        )}
        {!hideVoltage && (
          <Typography variant="caption" color="text.secondary" component="div">
            {voltage}V
          </Typography>
        )}
        {allLoads.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div">
            {allLoads.map((load, i) => (
              <span key={i}>
                {isBillboard ? `${load.name}: ${load.fixtureType} ×${load.fixtureCount}` : load.name}
                {i < allLoads.length - 1 && <br />}
              </span>
            ))}
          </Typography>
        )}
        {isBillboard && allActive && expectedReadings[dr.key] > 0 && (
          <Typography variant="caption" fontWeight={600} component="div">
            {t('measurements.expected')}: {expectedReadings[dr.key]}W
          </Typography>
        )}
        {allLoads.length === 0 && (
          <Typography variant="caption" color="text.secondary" component="div">
            {isBillboard ? t('measurements.noConfiguredPanels') : t('measurements.noConfiguredZones')}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default RelayInfoCard;
